import React, { useEffect } from "react";
import { PurchaseOrder } from "@/types/general";
import { FormProvider, useForm } from "react-hook-form";
import { LineGroupUnion, PurchaseOrdersFormTyping, purchaseOrdersScheme } from "./types";
import { zodResolver } from "@hookform/resolvers/zod";
import GeneralSection from "./parts/GeneralSection";
import ReferencesSection from "./parts/ReferencesSection";
import CurrencySection from "./parts/CurrencySection";
import BaseButton from "@reusables/BaseButton";
import AddressSection from "./parts/AddressSection";
import ProductSection from "./parts/ProductSection";
import dayjs from "dayjs";
import { useGetModuleCurrenciesQuery } from "@redux/features/currencies/currenciesApi";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { emptyFromBOM, emptyFromSO, emptyQPO } from "@redux/features/purchases/purchasesSlice";
import { normalizePrice } from "@helpers/utils";
import OrderReference from "./parts/OrderReference";
import LanguageSection from "./parts/LanguageSection";
import BaseCustomFieldsSection from "@reusables/BaseCustomFieldsSection";
import { isGroupOrder } from "../../types";
import { CopyHydrationProvider } from "./CopyHydrationContext";
import BaseInputsGrid from "@reusables/BaseInputsGrid";

interface MutationLayoutProperties {
    purchaseOrder: PurchaseOrder.Extended | undefined;
    copiedOrder?: PurchaseOrder.Extended;
    onSubmit: (args: PurchaseOrdersFormTyping) => void;
}

export default function MutationLayout(props: MutationLayoutProperties) {
    const { data: currenciesOptions = [] } = useGetModuleCurrenciesQuery("purchase-order");
    const baseCurrency = currenciesOptions.find((c) => c.isBase);

    const dispatch = useAppDispatch();
    const transitOrders = useAppSelector((state) => state.purchases.QPO);
    const fromSOOrders = useAppSelector((state) => state.purchases.fromSO);
    const fromBOMOrders = useAppSelector((state) => state.purchases.fromBOM);

    const form = useForm<PurchaseOrdersFormTyping>({
        resolver: zodResolver(purchaseOrdersScheme),
        defaultValues: {
            mode: "creation",
            is_editable: true,
            is_billing_for_delivery: false,
            purchase_date: dayjs(),
            lines: [],
            is_split_by_products: false,
            deleted_lines: [],
            reference_number: {},
        },
    });

    const buildPurchaseOrderLines = React.useCallback(
        (order: PurchaseOrder.Extended, isCopy: boolean): PurchaseOrdersFormTyping["lines"] =>
            order.lines.map((line) => {
                if (line.type === "line") {
                    return {
                        id: isCopy ? undefined : line.data.id,
                        product: line.data.product,
                        quantity: line.data.quantity,
                        unit_price: {
                            converted: normalizePrice(line.data.unit_price),
                            original: normalizePrice(line.data.unit_price / order.exchange_rate),
                        },
                        discount: line.data.discount,
                        tax: line.data.tax,
                    };
                }

                return {
                    id: isCopy ? undefined : line.data.id,
                    name: line.data.name,
                    comment: line.data.comment,
                    module: line.data.module,
                    module_entity_id: line.data.module_entity_id,
                    lines: line.data.lines.map((groupLine) => ({
                        id: isCopy ? undefined : groupLine.data.id,
                        product: groupLine.data.product,
                        quantity: groupLine.data.quantity,
                        unit_price: {
                            converted: normalizePrice(groupLine.data.unit_price),
                            original: normalizePrice(
                                groupLine.data.unit_price / order.exchange_rate
                            ),
                        },
                        discount: groupLine.data.discount,
                        tax: groupLine.data.tax,
                    })),
                };
            }),
        []
    );

    const hydratePurchaseOrder = React.useCallback(
        (order: PurchaseOrder.Extended, isCopy: boolean) => {
            form.setValue("mode", isCopy ? "creation" : "editing");
            form.setValue("is_editable", isCopy ? true : order.is_editable);

            form.setValue("supplier", order.supplier);
            form.setValue("purchase_date", isCopy ? dayjs() : order.purchase_date);
            form.setValue(
                "preferred_delivery_date",
                isCopy ? undefined : order.preferred_delivery_date
            );
            form.setValue("our_reference", order.our_reference);
            form.setValue("their_reference", order.their_reference);
            form.setValue("payment_terms", order.payment_terms);
            form.setValue("delivery_terms", order.delivery_terms);
            form.setValue("language", order.language);

            if (order.billing_address) {
                form.setValue("billing_address", {
                    name: order.billing_address.name,
                    street: order.billing_address.street,
                    city: order.billing_address.city,
                    country: order.billing_address.country,
                });
            }

            form.setValue("delivery_address", order.delivery_address);
            form.setValue("is_billing_for_delivery", order.is_billing_for_delivery);
            form.setValue("currency", order.currency);
            form.setValue("exchange_rate", order.exchange_rate);
            form.setValue("shipping_cost", order.shipping_cost);
            form.setValue("is_split_by_products", order.is_split_by_products);
            form.setValue("custom_fields", order.custom_fields ?? []);
            form.setValue("lines", buildPurchaseOrderLines(order, isCopy));
            form.setValue("reference_number", isCopy ? {} : order.reference_number ?? {});
            form.setValue("deleted_lines", []);
        },
        [buildPurchaseOrderLines, form]
    );

    useEffect(() => {
        if (props.purchaseOrder) {
            hydratePurchaseOrder(props.purchaseOrder, false);
        }
    }, [hydratePurchaseOrder, props.purchaseOrder]);

    useEffect(() => {
        if (props.copiedOrder) {
            hydratePurchaseOrder(props.copiedOrder, true);
        }
    }, [hydratePurchaseOrder, props.copiedOrder]);

    // // Needed for QUICK PUCHASE ORDER feature, used, for example, on restocking
    useEffect(() => {
        if (transitOrders.orders.length) {
            form.setValue(
                "lines",
                transitOrders.orders.map((line) => ({
                    ...line,
                    unit_price: {
                        converted: normalizePrice(line.unit_price ?? 0),
                        original: normalizePrice(line.unit_price ?? 0),
                    },
                }))
            );

            if (transitOrders.meta.supplier) {
                form.setValue("supplier", transitOrders.meta.supplier);
            }

            dispatch(emptyQPO());
        }
    }, [dispatch, form, transitOrders]);

    // // Needed for "PO FROM SO" feature, used, when sold products are low on stock
    useEffect(() => {
        if (fromSOOrders?.lines.length && fromSOOrders?.order) {
            form.setValue(
                "lines",
                fromSOOrders.lines.map((line) => {
                    if (isGroupOrder(line)) {
                        return {
                            ...line,
                            lines: line.lines.map((groupLine) => ({
                                ...groupLine,
                                unit_price: {
                                    converted: normalizePrice(groupLine.unit_price ?? 0),
                                    original: normalizePrice(groupLine.unit_price ?? 0),
                                },
                            })),
                        };
                    }

                    return {
                        ...line,
                        unit_price: {
                            converted: normalizePrice(line.unit_price ?? 0),
                            original: normalizePrice(line.unit_price ?? 0),
                        },
                    };
                })
            );

            form.setValue("reference_number", {
                id: fromSOOrders.order.id,
                code: fromSOOrders.order.code,
            });

            if (fromSOOrders.order.preferred_delivery_date) {
                form.setValue(
                    "preferred_delivery_date",
                    fromSOOrders.order.preferred_delivery_date.subtract(1, "day")
                );
            }

            if (fromSOOrders.order.our_reference) {
                form.setValue("our_reference", fromSOOrders.order.our_reference);
            }

            dispatch(emptyFromSO());
        }
    }, [dispatch, form, fromSOOrders]);

    // Needed for "PO FROM BOM" feature, used, when BOM is selected
    useEffect(() => {
        if (fromBOMOrders) {
            const { bom, components } = fromBOMOrders;

            const groupLine = [
                {
                    module_entity_id: bom.id,
                    name: bom.name,
                    module: "bom" as const,
                    lines: components.map((component) => ({
                        ...component,
                        unit_price: {
                            converted: normalizePrice(component.unit_price),
                            original: normalizePrice(component.unit_price),
                        },
                    })),
                },
            ] as PurchaseOrdersFormTyping["lines"];

            form.setValue("lines", groupLine as PurchaseOrdersFormTyping["lines"]);

            dispatch(emptyFromBOM());
        }
    }, [dispatch, form, fromBOMOrders]);

    useEffect(() => {
        if (!props.purchaseOrder && !props.copiedOrder && baseCurrency) {
            form.setValue("currency", baseCurrency);
        }
    }, [baseCurrency, form, props.copiedOrder, props.purchaseOrder]);

    const onSubmit = form.handleSubmit((data: PurchaseOrdersFormTyping) => {
        props.onSubmit(data);
    }, console.error);

    const copyHydrationValue = React.useMemo(
        () => ({
            isCopiedDraft: !!props.copiedOrder,
            copiedEntityId: props.copiedOrder?.supplier.id,
            copiedEntityType: props.copiedOrder ? ("supplier" as const) : null,
        }),
        [props.copiedOrder]
    );

    return (
        <CopyHydrationProvider value={copyHydrationValue}>
            <FormProvider {...form}>
                <OrderReference />
                <div className="space-y-[32px]">
                    <BaseInputsGrid cols={3}>
                        <GeneralSection />
                        <ReferencesSection />
                    </BaseInputsGrid>
                    <BaseInputsGrid cols={3}>
                        <CurrencySection />
                        <LanguageSection />
                    </BaseInputsGrid>
                    <BaseInputsGrid cols={3}>
                        <AddressSection />
                    </BaseInputsGrid>
                    <BaseCustomFieldsSection.Mutation
                        mode={props.purchaseOrder?.is_editable ? "editing" : "creation"}
                        module={"purchase_order"}
                    />
                    <ProductSection />
                    <div className="flex justify-center">
                        <BaseButton
                            text={"Save"}
                            size={"md"}
                            buttonWidth={"200px"}
                            onClick={() => onSubmit()}
                        />
                    </div>
                </div>
            </FormProvider>
        </CopyHydrationProvider>
    );
}
